import {
    ChangeInValueCellRenderer,
    ProgramTypeCellRenderer,
    KeywordAddBidCellRenderer,
    StatusBadgeCellRenderer,
    FilterTextFormatter,
} from "@/components/cell-renderers/AllCellRenderers";
import AgGridModalLayout from "@/components/wrappers/layouts/AgGridModalLayout";
import { useGetModulesData } from "@/hooks/AllCustomHooks";
import { TCommonRowData } from "@/ts/common-types";
import { DISABLED_MATCH_TYPES, KEYWORD_MATCH_TYPES, customFilterOptions } from "@/ts/constants";
import { isTarget, numericValueFormatter, titleCase } from "@/ts/utils";
import {
    GridApi,
    GridReadyEvent,
    RowNode,
    ValueFormatterParams,
} from "ag-grid-community";
import React from "react";
import { isNull } from "underscore";

interface IAddToExactProps {
    rowData: TCommonRowData;
    setAddToExactGridApi: React.Dispatch<React.SetStateAction<GridApi>>;
    onAddToExactSelectionChanged?: (event: GridReadyEvent) => void;
    loading: boolean;
}

const AddToExact: React.FC<IAddToExactProps> = ({
    rowData,
    setAddToExactGridApi,
    onAddToExactSelectionChanged,
    loading
}) => {
    const gridParams = useGetModulesData({
        type: "grid-params",
        subGridType: "addAsExact"
    });

    const columnDefs = useGetModulesData({
        type: "columns",
        subGridType: "addAsExact",
        colDefs: [
            {
                field: gridParams.keywordColumn || "keyword_name",
                headerName: "Keyword",
                minWidth: 200,
                maxWidth: 250,
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                checkboxSelection: true,
                tooltipField: gridParams.keywordColumn || "keyword_name",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value || ""),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                },
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
            },
            {
                headerName: gridParams[gridParams.bidColumn].headerName || "Bid",
                field: gridParams.bidColumn,
                filter: "agNumberColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: gridParams[gridParams.bidColumn].minWidth,
                maxWidth: gridParams[gridParams.bidColumn].maxWidth,
                cellRenderer: KeywordAddBidCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                    decimalPlace: 0,
                    origin: "smart-to-exact",
                },
            },
            {
                field: "campaign_name",
                headerName: "Campaign",
                filter: "agTextColumnFilter",
                tooltipField: "campaign_name",
                cellRenderer: StatusBadgeCellRenderer,
                minWidth: 160,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                    isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired
                },
            },
            {
                field: "ad_group_name",
                headerName: "Ad Group",
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["text"],
                },
                cellRenderer: StatusBadgeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                    isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired
                },
            },
            {
                field: "match_type",
                headerName: "Match Type",
                filter: "agSetColumnFilter",
                valueFormatter: (params: ValueFormatterParams) =>
                    titleCase(params.value),
                cellRenderer: ProgramTypeCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                minWidth: 100,
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPA",
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                cellRenderer: ProgramTypeCellRenderer,
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? params.value.toUpperCase() : params.value,
                filterParams: {
                    valueFormatter: FilterTextFormatter
                },
            },
            {
                headerName: "RoAS",
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
        ]
    });

    const isRowSelectable = (rowNode: RowNode) => {
        if (
            !KEYWORD_MATCH_TYPES.includes(
                rowNode.data.match_type.toLowerCase()
            ) &&
            isNull(
                isTarget(
                    rowNode.data.keyword_name
                )
            )
        )
            return false;

        return (
            !(DISABLED_MATCH_TYPES.includes(
                rowNode.data.match_type.toLowerCase()
            ))
        )
    }

    return (
        <div
            id="add-exact-to-smart-grid"
            className="ag-theme-alpine"
            style={{ height: "100%", width: "100%" }}
        >
            <AgGridModalLayout
                setGridApi={setAddToExactGridApi}
                rowData={rowData}
                columnDefs={columnDefs}
                onSelectionChanged={onAddToExactSelectionChanged}
                isTotalRowsReq={true}
                loading={loading}
                rowHeight={gridParams?.rowHeight}
                isRowSelectable={isRowSelectable}
            />
        </div>
    );
};

export default AddToExact;
